// File: frontend/src/views/public/NewsletterUnsubscribe.js

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_CONFIG } from '../../config/api';
import apiService from '../../services/apiService';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import styles from './NewsletterUnsubscribe.module.css';

const NewsletterUnsubscribe = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user_id');
  const token = searchParams.get('token');
  const correlationId = searchParams.get('correlation_id');
  
  const [status, setStatus] = useState('pending'); // pending, confirming, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const [countdown, setCountdown] = useState(5);
  
  const hasValidated = useRef(false);
  
  useEffect(() => {
    document.title = 'Unsubscribe from Newsletter - Allegory Insurance';
    
    if (hasValidated.current) {
      return;
    }
    
    validateUnsubscribeLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token]);
  
  // Countdown for redirect after success
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      navigate('/');
    }
  }, [status, countdown, navigate]);
  
  const validateUnsubscribeLink = async () => {
    hasValidated.current = true;
    
    if (!userId || !token) {
      setStatus('error');
      setErrorMessage('Invalid unsubscribe link. Please check the link from your email.');
      return;
    }
    
    // Link is valid, show confirmation screen
    setStatus('pending');
  };
  
  const handleConfirmUnsubscribe = async () => {
    setStatus('confirming');
    
    try {
      console.log('📧 Unsubscribing from newsletter...', { 
        user_id: userId,
        has_token: !!token,
        correlation_id: correlationId
      });
      
      const response = await apiService.unsubscribeNewsletter({
        user_id: userId,
        enterprise_id: API_CONFIG.enterpriseId,
        token: token,
        language: navigator.language.toLowerCase() || 'en-ca',
        correlation_id: correlationId
      });
      
      if (response.success) {
        console.log('✅ Successfully unsubscribed');
        setUserData(response.data);
        setStatus('success');
      } else {
        throw new Error(response.error || 'Unsubscribe failed');
      }
      
    } catch (error) {
      console.error('❌ Unsubscribe error:', error);
      setStatus('error');
      
      if (error.message.includes('Invalid or expired')) {
        setErrorMessage('This unsubscribe link is invalid or has expired. Please use the latest link from your email.');
      } else if (error.message.includes('already unsubscribed')) {
        setErrorMessage("You're already unsubscribed from our newsletter.");
      } else {
        setErrorMessage(error.message || 'Failed to unsubscribe. Please try again or contact support.');
      }
    }
  };
  
  const handleBackToHome = () => {
    navigate('/');
  };
  
  const handleContactSupport = () => {
    navigate('/contact');
  };
  
  // Confirming state (processing)
  if (status === 'confirming') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.brandHeader}>
            <span className={styles.logo}>💳</span>
            <h2 className={styles.brandName}>Allegory Insurance</h2>
          </div>
          <LoadingSpinner />
          <p className={styles.processingText}>Processing your request...</p>
        </div>
      </div>
    );
  }
  
  // Success state
  if (status === 'success') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.brandHeader}>
            <span className={styles.logo}>💳</span>
            <h2 className={styles.brandName}>Allegory Insurance</h2>
          </div>
          
          <div className={styles.successIcon}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#FEF3C7"/>
              <path 
                d="M12 8V12M12 16H12.01" 
                stroke="#F59E0B" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
            </svg>
          </div>
          
          <h1 className={styles.title}>You've Been Unsubscribed</h1>
          <p className={styles.message}>
            {userData?.first_name && `${userData.first_name}, `}
            you've been successfully unsubscribed from our newsletter. We're sad to see you go!
          </p>
          
          <div className={styles.infoBox}>
            <p className={styles.infoText}>
              You will no longer receive our weekly insurance tips, rate alerts, and exclusive savings opportunities.
            </p>
          </div>
          
          <p className={styles.feedbackText}>
            Changed your mind? You can always resubscribe from our homepage.
          </p>
          
          <div className={styles.countdownBox}>
            <p className={styles.redirectMessage}>
              Redirecting to homepage in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''}...
            </p>
          </div>
          
          <button 
            onClick={handleBackToHome}
            className={styles.primaryButton}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }
  
  // Error state
  if (status === 'error') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.brandHeader}>
            <span className={styles.logo}>💳</span>
            <h2 className={styles.brandName}>Allegory Insurance</h2>
          </div>
          
          <div className={styles.errorIcon}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#FEF2F2"/>
              <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          
          <h1 className={styles.title}>Unsubscribe Failed</h1>
          <div className={styles.errorBox}>
            <p className={styles.errorMessage}>{errorMessage}</p>
          </div>
          
          <p className={styles.helpText}>
            If you continue to have issues, please contact our support team and we'll help you right away.
          </p>
          
          <div className={styles.buttonGroup}>
            <button 
              onClick={handleBackToHome}
              className={styles.primaryButton}
            >
              Back to Home
            </button>
            
            <button 
              onClick={handleContactSupport}
              className={styles.secondaryButton}
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Pending state (confirmation screen)
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.brandHeader}>
          <span className={styles.logo}>💳</span>
          <h2 className={styles.brandName}>Allegory Insurance</h2>
        </div>
        
        <div className={styles.warningIcon}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#FEF3C7"/>
            <path 
              d="M12 8V12M12 16H12.01" 
              stroke="#F59E0B" 
              strokeWidth="2" 
              strokeLinecap="round"
            />
          </svg>
        </div>
        
        <h1 className={styles.title}>Unsubscribe from Newsletter?</h1>
        <p className={styles.message}>
          Are you sure you want to unsubscribe from our newsletter? You'll no longer receive:
        </p>
        
        <div className={styles.benefitsList}>
          <div className={styles.benefitItem}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Weekly insurance tips and advice</span>
          </div>
          <div className={styles.benefitItem}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Rate alerts and price drop notifications</span>
          </div>
          <div className={styles.benefitItem}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Exclusive savings opportunities</span>
          </div>
        </div>
        
        <div className={styles.buttonGroup}>
          <button 
            onClick={handleConfirmUnsubscribe}
            className={styles.dangerButton}
          >
            Yes, Unsubscribe Me
          </button>
          
          <button 
            onClick={handleBackToHome}
            className={styles.secondaryButton}
          >
            No, Keep Me Subscribed
          </button>
        </div>
        
        <p className={styles.footerNote}>
          You can always resubscribe later from our homepage.
        </p>
      </div>
    </div>
  );
};

export default NewsletterUnsubscribe;